import { useMemo } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import Konva from 'konva';
import { AppDispatch, RootState } from 'store';
import { useRefs } from 'contexts/RefContext';
import { setUserHasClickedSave, setStageRotation, setZoomLevel } from 'store/slices/ToolSlice';
import { STANDARD_IMAGERY_SIZE } from 'constants/index';
import { getImagerySourceLabel, getSolarDataSource } from '../../util';
import { RoofImageBuilder } from 'components/tool/utils/konvaUtil';
import { updateDesing } from 'pages/LocationMap/api';

import { MonthlyEnergyPerSegment, perPanelEnergyDetails } from 'components/DisplayEnergy/types';
import { getMonthlyDetails, deleteMonthlyDetails, getHourlyEnergy } from 'components/DisplayEnergy/api';
import {
	adjustScalingWhileSavingDesignImage,
	getEnergyData,
	getIsApprovalRequired,
	setZoomlevelOnSave,
} from 'components/DisplayEnergy/util';
import { EnergyState } from 'store/slices/EnergySlice/types';

// Two animation frames let the browser paint the north-up reset before the save work starts.
// A hidden tab, or an iframe that's off screen (the new-proposal auto-save), gets no frames at
// all, so never wait on them for more than a moment or the save hangs on its loader.
const PAINT_WAIT_MAX_MS = 100;

function waitForPaint(): Promise<void> {
	if (document.hidden) return Promise.resolve(); // nothing to paint
	return new Promise(resolve => {
		const timeoutId = setTimeout(resolve, PAINT_WAIT_MAX_MS);
		requestAnimationFrame(() => requestAnimationFrame(() => {
			clearTimeout(timeoutId);
			resolve();
		}));
	});
}

export function useSaveSolarDesign() {
	const dispatch = useDispatch<AppDispatch>();
	const { konvaRef } = useRefs();

	const {
		uuid, roofSegs, roofSegsFromWhichUserHasSelectedPanels, panel, org,
		imgHeight, imgWidth, selectedPanelKey, showAllPanels, latLng, mode,
		imagerySource, isSunroofAvailable, allRoofSegs, groundMetersPerPixel,
	} = useSelector((state: RootState) => state.roofData.data);

	const { zoomLevel, fullScreenEnabled } = useSelector(
		(state: RootState) => state.toolNewPostions.data
	);
	const energy = useSelector(
		(state: RootState) => state.energyData.data as unknown as EnergyState
	);
	const { energyDerateRate } = useSelector(
		(state: RootState) => state.QueryParams.data
	);

	const averageRoofHeight = useMemo(() => {
		const hasMissingHeight = allRoofSegs.some(
			(seg) => !seg?.noGapGroundMount && seg.roofHeight == null
		);
		if (hasMissingHeight) return null;
		const segs = allRoofSegs.filter((s) => !s.noGapGroundMount && s.roofHeight != null);
		if (segs.length === 0) return null;
		return segs.reduce((acc, seg) => acc + (seg.roofHeight ?? 0), 0) / segs.length;
	}, [allRoofSegs]);

	const roofSummary = useMemo(
		() => Object.values(allRoofSegs).map((seg) => ({
			label: seg.label || '',
			area: seg.area || 0,
			pitch: seg.pitchDegrees || 0,
			azimuth: seg.azimuthDegrees || 0,
		})),
		[allRoofSegs]
	);

	const getMonthlyDetailsQuery = useQuery(
		`monthlyDetails-${uuid}`,
		async () => {
			if (!allRoofSegs?.length) return;
			let retryCount = 0;
			let allPanelsMonthlyDetails: { [key: string]: perPanelEnergyDetails } = {};
			while (retryCount <= 2) {
				const monthlyData = await getMonthlyDetails(uuid, selectedPanelKey);
				allPanelsMonthlyDetails = {};
				let panelMismatches = 0;
				monthlyData?.forEach((segment: MonthlyEnergyPerSegment) => {
					Object.values(segment.perPanelEnergyDetails).forEach((p) => {
						allPanelsMonthlyDetails[p.panelId] = p;
					});
				});
				energy.selectedPanels.forEach((p: string) => {
					if (!Object.keys(allPanelsMonthlyDetails).includes(p)) panelMismatches++;
				});
				if (panelMismatches > 0) {
					await deleteMonthlyDetails(uuid, selectedPanelKey);
					retryCount += 1;
				} else {
					break;
				}
			}
			return allPanelsMonthlyDetails;
		},
		{ cacheTime: 0, refetchOnWindowFocus: false, enabled: false, retry: 1 }
	);

	const getHourlyEnergyQuery = useQuery(
		`hourlyEnergy-${uuid}`,
		async () => {
			if (!allRoofSegs?.length) return;
			return getHourlyEnergy(uuid, selectedPanelKey);
		},
		{ cacheTime: 0, refetchOnWindowFocus: false, enabled: false, retry: 1 }
	);

	const mutation = useMutation('save-solar-design', async () => {
		// Reset the live view to north + centered FIRST, before any network round-trip — not
		// after updateDesing resolves. Otherwise, however far the user panned/rotated stays frozen
		// on screen for however long that API call takes, instead of resetting right away; a far
		// enough pan previously could also make the capture below come out blank.
		dispatch(setUserHasClickedSave({ enabled: true }));
		const stage = konvaRef?.current as Konva.Stage;
		adjustScalingWhileSavingDesignImage(stage, imgHeight);
		// adjustScalingWhileSavingDesignImage mutates the live stage directly (rotation/scale/
		// position) without touching Redux. rotation/scaleX/scaleY are React-controlled props on
		// <Stage>, so if anything re-renders before this save finishes, React would reconcile them
		// back to the stale (still-rotated) Redux values while position stays at the just-reset
		// spot — a visible flash/disappearance. Keep Redux in lockstep with what was just applied.
		dispatch(setStageRotation({ rotation: stage.rotation() }));
		// setZoomLevel also writes konvaStagePosition (see the reducer), so this covers both.
		dispatch(setZoomLevel({ scaleBy: stage.scaleX(), position: stage.position() }));
		stage.batchDraw();

		// The reset above is applied instantly, but the awaits below (energy refetch, save API
		// call) and RoofImageBuilder's full canvas exports can together block/occupy the main
		// thread for a few seconds — the browser can't actually paint the reset (north-up) frame
		// until it gets a free moment, so whatever was on screen before save (e.g. a rotated view)
		// would otherwise stay frozen throughout instead of updating right away. Yield across two
		// animation frames first so the browser commits the reset paint before any of that begins.
		await waitForPaint();

		const monthly = getMonthlyDetailsQuery.data ?? (await getMonthlyDetailsQuery.refetch()).data;
		const hourly = getHourlyEnergyQuery.data ?? (await getHourlyEnergyQuery.refetch()).data;
		const offset = Math.round((energy.deratedEnergy / energy.billEnergy) * 100);

		const { design, selectedPanels } = getEnergyData(
			energy, roofSegsFromWhichUserHasSelectedPanels, roofSegs, offset,
			panel, monthly, uuid, energyDerateRate, hourly,
		);
		const { facets, ...productionData } = design;

		const designSaveObj = {
			selectedPanels: Array.from(selectedPanels),
			productionData: {
				energy: productionData.energy,
				systemSize: productionData.systemSize,
				offset: productionData.offset,
				totalPanels: productionData.totalPanels,
				energyDerateRate,
			},
			billAmount: Number(productionData.billAmount),
			annualEnergy: productionData.annualUsage,
			panelKey: productionData.panelKey,
			showToggleForAllPanels: showAllPanels,
			toolEditConfig: { zoomLevel: setZoomlevelOnSave(zoomLevel, imgHeight) },
			source: imagerySource,
		};
		const { bbox } = await updateDesing(designSaveObj, uuid);

		const [lat, lng] = latLng.split(',');
		const pixelRatio = imgHeight ? Math.max(STANDARD_IMAGERY_SIZE / imgHeight, 0.8) : 0.8;
		const images = new RoofImageBuilder({
			stage,
			fullScreenEnabled,
			groundMetersPerPixel,
			ogImgDims: { width: imgWidth, height: imgHeight },
			houseBoundingBox: bbox,
			filteredRoofIds: Object.keys(roofSegs),
		})
			.withOutput({ pixelRatio, mimeType: 'image/png' })
			.getFullImage({ mimeType: org.setting?.isB2C ? 'image/png' : 'image/jpeg' })
			.getSolarLinrFeetNodeImage()
			.getLabelImage()
			.getLabelImageOfFilteredRoofSegments()
			.result();

		const isFlatFacet = design.facets.some(
			(f: { isGroundMount?: boolean; panelsCount?: number }) => !!f?.isGroundMount && f.panelsCount !== 0
		);

		const solarDataSource = getSolarDataSource(mode, { hasFlatFacet: isFlatFacet });

		return {
			design: {
				...design,
				isSunroofAvailable,
				isApprovalRequired: getIsApprovalRequired(isSunroofAvailable, mode),
				img: images.baseImage,
				linearFtImage: images.solarLinerFtOnly,
				imgWithFacetLabel: images.filteredLabelsOnly,
				imgWithFacetlabelForAllRoofSegs: images.labelsOnly,
				lat: +lat, lng: +lng,
				imagerySource: getImagerySourceLabel(imagerySource),
				solarDataSource: solarDataSource.key,
				solarDataSourceLabel: solarDataSource.label,
				averageRoofHeight,
				roofSummary,
				totalArea: roofSummary.reduce((acc, curr) => acc + curr.area, 0),
			},
			onlySync: false,
		};
	});

	return {
		saveSolarDesign: mutation.mutateAsync,
		status: { isLoading: mutation.isLoading, isError: mutation.isError },
	};
}
